import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faAndroid, faApple, faAws, faReact, faGoogle, faFlutter, faPython, faNodeJs, faDocker 
} from '@fortawesome/free-brands-svg-icons';
import { 
  faLaptopCode, faCogs, faShieldAlt, faMicrochip, faUsersViewfinder, faRobot,
  faMagnifyingGlass, faPenRuler, faCode, faVial, faRocket,
  faWrench, faRotate, faBugSlash, faFileLines
} from '@fortawesome/free-solid-svg-icons';
import './Services.css';
import Portfolio from './Portfolio';

const Services = () => {
  const mainServices = [
    { 
      title: "Ecosistemas Web Pro", 
      desc: "Plataformas de alto rendimiento, PWAs y dashboards con arquitectura modular y escalable.", 
      icon: faLaptopCode 
    },
    { 
      title: "Software Industrial & ERP", 
      desc: "Sistemas a medida que controlan procesos críticos de manufactura, logística y administración.", 
      icon: faCogs 
    },
    { 
      title: "Modernización & Evolución de Sistemas", 
      desc: "Actualización tecnológica, migración de código legado y refactorización para plataformas existentes.", 
      icon: faRotate 
    },
    { 
      title: "Continuidad Operativa & Soporte L3", 
      desc: "Diagnóstico profundo, resolución crítica de incidencias, corrección de bugs y monitoreo de estabilidad.", 
      icon: faBugSlash 
    },
    { 
      title: "Ciberseguridad & DevOps", 
      desc: "Automatización de despliegues (CI/CD), hardening y arquitecturas resilientes bajo estándares globales.", 
      icon: faShieldAlt 
    },
    { 
      title: "Conectividad IoT & Hardware", 
      desc: "Integración de software con sensores, telemetría, control de acceso y dispositivos embebidos.", 
      icon: faMicrochip 
    },
    { 
      title: "Big Data & Analytics", 
      desc: "Procesamiento masivo para análisis de comportamiento de usuarios y modelos predictivos.", 
      icon: faUsersViewfinder 
    },
    { 
      title: "IA & Automatización", 
      desc: "Modelos inteligentes aplicados a la optimización de flujos operativos y tareas complejas.", 
      icon: faRobot 
    }
  ];

  const steps = [
    { id: "01", name: "Discovery & Auditoría", desc: "Levantamiento de requerimientos, análisis de deuda técnica y arquitectura base.", icon: faMagnifyingGlass },
    { id: "02", name: "Diseño & Especificación", desc: "Modelado funcional, diagramación técnica y experiencia de usuario.", icon: faPenRuler },
    { id: "03", name: "Ingeniería & Código", desc: "Desarrollo y refactorización con estándares limpios, tipados y estructurados.", icon: faCode },
    { id: "04", name: "QA, Stress & Testing", desc: "Validaciones rigurosas funcionales, de rendimiento y mitigación de fallos.", icon: faVial },
    { id: "05", name: "Despliegue & Gobernanza", desc: "Puesta en producción controlada y formalización del conocimiento técnico.", icon: faRocket }
  ];

  const techs = [
    { name: 'React', icon: faReact },
    { name: 'Flutter', icon: faFlutter },
    { name: 'AWS', icon: faAws },
    { name: 'Firebase', icon: faGoogle },
    { name: 'Python', icon: faPython },
    { name: 'Node.js', icon: faNodeJs },
    { name: 'Docker', icon: faDocker },
    { name: 'Android', icon: faAndroid },
    { name: 'iOS', icon: faApple }
  ];

  return (
    <section id="servicios" className="services-page">
      <div className="bg-glow-top"></div>
      <motion.div 
        className="services-header"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="main-title">Ingeniería de Software Global</h2>
        <p className="main-subtitle">Construcción, modernización y soporte crítico para infraestructuras tecnológicas de alto rendimiento.</p>
      </motion.div>

      <div className="services-grid">
        {mainServices.map((service, index) => (
          <motion.div 
            key={index} 
            className="service-card glass-card"
            whileHover={{ y: -8 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            viewport={{ once: true }}
          >
            <FontAwesomeIcon icon={service.icon} className="service-icon" />
            <h3 className="service-card-title">{service.title}</h3>
            <p className="service-card-desc">{service.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="flujo-section">
        <h2 className="section-title-centered">Nuestro Flujo Metodológico</h2>
        <div className="flujo-grid">
          {steps.map((step, index) => (
            <motion.div 
              key={index} 
              className="flujo-item"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flujo-icon-wrapper">
                <span className="flujo-number-bg">{step.id}</span>
                <FontAwesomeIcon icon={step.icon} className="flujo-icon-overlay" />
              </div>
              <h4>{step.name}</h4>
              <p>{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <Portfolio />   

      <div className="capacidades-section">
        <div className="capacidades-container glass-card">
          <h2 className="section-title-centered">Capacidades Corporativas</h2>
          <div className="capacidades-grid">
            <div className="capacidad-item">
              <h3 className="capacidad-title">Evolución & Mantenimiento Proactivo</h3>
              <p className="capacidad-desc">Intervención precisa en bases de código heredadas, optimizando dependencias, tiempos de respuesta y estabilidad global sin interrumpir operaciones.</p>
            </div>
            <div className="capacidad-item">
              <h3 className="capacidad-title">Estandarización & Arquitectura Limpia</h3>
              <p className="capacidad-desc">Estructuración formal de sistemas, garantizando trazabilidad técnica, transferibilidad de conocimiento y facilidad para auditorías tecnológicas.</p>
            </div>
            <div className="capacidad-item">
              <h3 className="capacidad-title">Cumplimiento, SLA & Seguridad</h3>
              <p className="capacidad-desc">Garantía de respuesta para incidentes operativos bajo estrictos estándares de seguridad de la información y disponibilidad continua.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="tech-stack-section">
        <h3 className="tech-title">Tecnologías de Vanguardia</h3>
        <div className="tech-flex">
          {techs.map((tech, index) => (
            <motion.div 
              key={index} 
              className="tech-badge"
              whileHover={{ scale: 1.1, backgroundColor: "rgba(0, 210, 255, 0.1)" }}
            >
              <FontAwesomeIcon icon={tech.icon} className="tech-icon" />
              <span className="tech-label">{tech.name}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;